import React, { FC, useState, useMemo, useEffect } from "react";
import { Checkbox } from "@mui/material";
import imagePlaceholder from "../../../assets/svg/image_placeholder.svg";
import { ReactComponent as KeyDateCheckedIcon } from "../../../assets/svg/key-date-checked.svg";
import { ReactComponent as KeyDateUncheckedIcon } from "../../../assets/svg/key-date-unchecked.svg";
import { BrandProject } from "./constants";
import { formatKeyProjectDate, getProjectDatesForDisplay, BRAND_PROJECT_STATUSES } from "../helper";
import { addDays, formatDate, HorizontalMilestoneStepper, STEP_ICONS, SchedulingIcon } from "../../../components/ProjectTimeline";
import { toDayDate } from "../../../utils/filmWindow";
import { PricingBreakdown } from "../../../components/Pricing/PricingBreakdown";
import { useUser } from "../../../hooks/useUser";
import { useProductDetailLink } from "../../../hooks/useProductDetailLink";
import { hidePricing } from "../../../utils";

const getEntry = (label: string, value: string | number) => (
    <div className={`flex flex-grow flex-col gap-2 ${label === "Description" ? "w-full" : ""}`}>
        <p className="text-text-1 text-base font-medium">{label}</p>
        <p className="text-text-2 text-sm">{value || "—"}</p>
    </div>
);

interface ProductDetailsCardProps {
    project: BrandProject;
}

export const ProductDetailsCard: FC<ProductDetailsCardProps> = ({ project }) => {
    const products = (project as any)?.products as
        | Array<{
              image?: string;
              title?: string;
              short_description?: string;
              desireid?: number;
              description?: string;
          }>
        | undefined;
    const product = project?.product as
        | {
              image?: string;
              title?: string;
              short_description?: string;
              desireid?: number;
          }
        | undefined;
    const rmnBrand = project?.brands?.find((b: any) => b.rmn === true);
    const nonRmnBrand = project?.brands?.find((b: any) => !b.rmn);

    // Use products array if available, otherwise fall back to single product
    const productList = products && products.length > 0 ? products : product ? [product] : [];

    const [activeIndex, setActiveIndex] = useState(0);

    // Clamp activeIndex when productList changes
    useEffect(() => {
        if (activeIndex >= productList.length) {
            setActiveIndex(0);
        }
    }, [productList.length, activeIndex]);

    const { goToProduct } = useProductDetailLink();
    const activeProd = productList[activeIndex];
    const productName = activeProd?.title || project?.title || "—";
    const productImage = activeProd?.image || undefined;
    const description =
        activeProd?.short_description || (activeProd as any)?.description || project?.description || "—";
    const idNumber = activeProd?.desireid ?? project?.desireid ?? "—";
    const canOpenProduct = activeProd?.desireid != null && (nonRmnBrand as any)?.id != null;
    const openProduct = () =>
        goToProduct({ desireid: activeProd?.desireid, brand_id: (nonRmnBrand as any)?.id });

    return (
        <div>
            <h2 className="text-lg font-extrabold leading-[120%] text-text-1 mb-4">Product Details</h2>
            {productList.length === 0 ? (
                <div className="flex flex-col gap-6 p-6 border border-border-token rounded-2xl bg-surface">
                    <p className="text-text-2 text-sm">No products assigned</p>
                    {rmnBrand && getEntry("Retail Media Network", rmnBrand?.name ?? "—")}
                    {getEntry("Brand", nonRmnBrand?.name ?? "—")}
                </div>
            ) : (
                <div className="flex flex-col gap-0">
                    {/* Tabs — only show when more than one product */}
                    {productList.length > 1 && (
                        <div
                            className="flex gap-0 border-b border-border-token mb-0 overflow-x-auto scrollbar-hide"
                            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                        >
                            {productList.map((prod, index) => {
                                const isActive = index === activeIndex;
                                return (
                                    <button
                                        key={prod?.desireid ?? index}
                                        type="button"
                                        onClick={() => setActiveIndex(index)}
                                        title={prod?.title || `Product ${index + 1}`}
                                        className={`px-4 py-2 text-sm font-medium transition-colors shrink-0 max-w-[200px] truncate ${
                                            isActive
                                                ? "text-accent-text border-b-2 border-accent -mb-[1px]"
                                                : "text-text-2 hover:text-text-1"
                                        }`}
                                    >
                                        {prod?.title || `Product ${index + 1}`}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Active product card */}
                    <div className="flex flex-row flex-wrap gap-6 p-6 border border-border-token rounded-2xl bg-surface">
                        {/* White plate: uploaded product art has white baked in,
                            so a deliberate plate beats a ragged edge. */}
                        {productImage && (
                            <div className="flex flex-col flex-grow w-full justify-left bg-white rounded-2xl overflow-hidden p-2">
                                <img
                                    src={productImage}
                                    alt={String(productName)}
                                    onClick={canOpenProduct ? openProduct : undefined}
                                    className={`max-w-full max-h-[200px] object-contain rounded-2xl${
                                        canOpenProduct ? " cursor-pointer" : ""
                                    }`}
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = imagePlaceholder;
                                    }}
                                />
                            </div>
                        )}
                        <div className="flex flex-grow w-full flex-col gap-2">
                            <p
                                onClick={canOpenProduct ? openProduct : undefined}
                                className={`text-[var(--Base-1000,#000)] text-base not-italic font-black leading-[132%]${
                                    canOpenProduct ? " cursor-pointer hover:text-accent-text" : ""
                                }`}
                            >
                                {productName}
                            </p>
                            <div className="border-b border-border-token w-full mt-[8px]" />
                        </div>
                        {rmnBrand && getEntry("Parent Network", rmnBrand?.name ?? "—")}
                        {getEntry("Brand", nonRmnBrand?.name ?? "—")}
                        {getEntry("ID Number", String(idNumber))}
                        {getEntry("Description", description)}
                    </div>
                </div>
            )}
        </div>
    );
};

const MILESTONES: {
    key: string;
    label: string;
    getDate: (p: BrandProject) => string | undefined;
}[] = [
    {
        key: "productShippedToTdc",
        label: "Shipped to TDC",
        getDate: (p) => p?.product_shipped_to_tdc_at
    },
    {
        key: "productReceivedByTdc",
        label: "Received by TDC",
        getDate: (p) => p?.product_received_by_tdc_at
    },
    {
        key: "productShippedToExpert",
        label: "Shipped to Expert",
        getDate: (p) => p?.product_shipped_to_expert_at
    },
    {
        key: "productReceivedByExpert",
        label: "Received by Expert",
        getDate: (p) => p?.product_received_by_expert_at
    }
];

interface KeyProjectDatesCardProps {
    project: BrandProject;
}

export const KeyProjectDatesCard: FC<KeyProjectDatesCardProps> = ({ project }) => {
    const initialChecked = useMemo(
        () =>
            MILESTONES.reduce<Record<string, boolean>>((acc, { key, getDate }) => {
                const dateRaw = getDate(project);
                acc[key] = formatKeyProjectDate(dateRaw) !== "Unavailable";
                return acc;
            }, {}),
        [project]
    );
    const [checked, setChecked] = useState<Record<string, boolean>>(initialChecked);
    useEffect(() => {
        setChecked(initialChecked);
    }, [initialChecked]);

    return (
        <div>
            <h2 className="text-lg font-extrabold leading-[120%] text-text-1 mb-4">Key Shipping Dates</h2>
            <div className="flex flex-col gap-6 p-6 border border-border-token rounded-2xl bg-surface">
                {MILESTONES.map(({ key, label, getDate }) => {
                    const dateRaw = getDate(project);
                    const dateStr = formatKeyProjectDate(dateRaw);
                    const isChecked = dateStr !== "Unavailable";
                    return (
                        <div key={key} className="flex flex-grow items-start gap-[20px]">
                            <Checkbox
                                icon={<KeyDateUncheckedIcon className="shrink-0 mt-2" />}
                                checkedIcon={<KeyDateCheckedIcon className="shrink-0 mt-2" />}
                                checked={isChecked}
                                onChange={() =>
                                    setChecked((prev) => ({
                                        ...prev,
                                        [key]: !prev[key]
                                    }))
                                }
                                disableRipple
                                className="p-0 mt-0.5 shrink-0 w-6 h-6 min-w-6 min-h-6"
                                sx={{
                                    padding: 0,
                                    width: 24,
                                    height: 24,
                                    minWidth: 24,
                                    minHeight: 24,
                                    "&:hover": { backgroundColor: "transparent" },
                                    "& .MuiTouchRipple-root": { display: "none" }
                                }}
                                size="small"
                            />
                            <div className="flex flex-col gap-2">
                                <p className="text-text-1 text-base font-medium">{label}</p>
                                <p className="text-text-2 text-sm">{dateStr}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─── Project Timeline Card ────────────────────────────────────────────────────

type MilestoneState = "actual" | "unavailable" | "estimated" | "committed";

interface MilestoneDisplay {
    label: string;
    date: Date | null;
    state: MilestoneState;
}

export const ProjectTimelineCard: FC<{ project: BrandProject }> = ({ project }) => {
    if (!project.start_date) return null;

    const startDate = new Date(project.start_date);

    const isDelivered = project.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED;

    // Resolve actual dates from project fields
    const brief = project.briefs?.[0];
    const actualBriefSubmitted = brief?.submitted_at ? new Date(brief.submitted_at) : null;
    const actualBriefApproval = brief?.approved_at ? new Date(brief.approved_at) : null;
    const actualExpertSelection = project.expert_selection_at ? new Date(project.expert_selection_at) : null;
    const actualExpertSelected = project.expert_selected_at ? new Date(project.expert_selected_at) : null;
    // Pre-Production milestone: film_date_scheduled_at is stamped when the
    // selection enters Pre-Production (and re-stamped on a reschedule).
    const actualScheduled = project.film_date_scheduled_at ? new Date(project.film_date_scheduled_at) : null;
    // Scheduling starts the instant the experts are selected (the API moves
    // the project to Scheduling on that same save), so both milestones read
    // the same stamp. There is no separate scheduling_at column.
    const actualScheduling = actualExpertSelected;
    // film_date/film_date_end are UTC-midnight day markers — read as calendar
    // days (toDayDate), never as local instants.
    const actualFilming = toDayDate(project.film_date);
    const filmingEnds = toDayDate(project.film_date_end) || actualFilming;
    const actualEditing = filmingEnds ? addDays(filmingEnds, 1) : null;
    // Delivery: channel-based projects (content_channel_id set) use the earliest
    // delivery_date across all files with no fallback. Pre-channel projects (no
    // content_channel_id) use the master file's delivery_date with a createdAt
    // fallback since master.createdAt reliably reflects when the file was uploaded.
    const files = project.projectFiles ?? [];
    const isChannelBased = files.some((f) => f.content_channel_id != null);
    let actualDelivery: Date | null = null;
    if (isChannelBased) {
        actualDelivery =
            files
                .map((f) => (f.delivery_date ? new Date(f.delivery_date) : null))
                .filter((d): d is Date => d !== null)
                .sort((a, b) => a.getTime() - b.getTime())[0] ?? null;
    } else {
        const masterFile = files.find((f) => f.master) ?? files[0];
        actualDelivery = masterFile?.delivery_date
            ? new Date(masterFile.delivery_date)
            : isDelivered && masterFile?.createdAt
            ? new Date(masterFile.createdAt)
            : null;
    }

    // earliest_delivery_date is empty until internal staff set it in Edit
    // Project; until then the Earliest Possible Delivery step is the estimate.
    // Once set it always shows, delivered or not.
    const committedDelivery = toDayDate(project.earliest_delivery_date);

    // Step 1 (Video Requested) is always actual — start_date is the anchor
    // Actuals in milestone order (null = no actual date available for that step)
    const allActuals: (Date | null)[] = [
        startDate, // 0 — Video Requested (always actual)
        actualBriefSubmitted, // 1 — Brief Submitted
        actualBriefApproval, // 2 — Brief Approval
        actualExpertSelection, // 3 — Expert Selection
        actualExpertSelected, // 4 — Expert Selected
        actualScheduling, // 5 — Scheduling
        actualScheduled, // 6 — Pre-Production
        actualFilming, // 7 — Filming
        actualEditing, // 8 — Editing (derived from film_date + 1)
        null, // 9 — Earliest Possible Delivery (committed or estimated, never actual)
        actualDelivery // 10 — Actual Delivery (earliest deliverable delivery_date)
    ];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const filmDateOnly = actualFilming ? new Date(actualFilming.getTime()) : null;
    if (filmDateOnly) filmDateOnly.setHours(0, 0, 0, 0);
    const filmingLabel = filmDateOnly && today > filmDateOnly ? "Filmed" : "Filming";

    const pastEditingStatuses = [
        BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED,
        BRAND_PROJECT_STATUSES.FINAL_DELIVERED
    ];
    const editingLabel = pastEditingStatuses.includes(project.status as any) ? "Edited" : "Editing";

    const allLabels = [
        "Video Requested",
        "Brief Submitted",
        "Brief Approval",
        "Expert Selection",
        "Expert Selected",
        "Scheduling",
        "Pre-Production",
        filmingLabel,
        editingLabel,
        "Earliest Possible Delivery",
        "Actual Delivery"
    ];

    // Inter-milestone offsets (each relative to previous step, total = 26d from start).
    // Scheduling is 0: it begins the same day the experts are selected.
    // Actual Delivery (idx 10) is never estimated.
    const allOffsets = [0, 2, 3, 5, 5, 0, 1, 1, 1, 8, 0];

    // Hide Expert Selection (idx 3), Expert Selected (idx 4) and Scheduling
    // (idx 5) when the project doesn't involve experts: Scheduling is dated
    // from expert_selected_at, which an experts-off project never gets.
    // `experts === false` is the explicit off signal; undefined / true /
    // missing keep the steps visible.
    const hideExpertSteps = (project as any).experts === false;
    // Actual Delivery (idx 10) replaces Earliest Possible Delivery (idx 9)
    // once a deliverable carries a delivery date. A Final Delivered project
    // without one keeps both, Actual Delivery as N/A.
    const showActualDelivery = actualDelivery !== null || isDelivered;
    const showEarliestDelivery = actualDelivery === null;
    const keep = (_: unknown, i: number) =>
        !(hideExpertSteps && (i === 3 || i === 4 || i === 5)) &&
        !(i === 9 && !showEarliestDelivery) &&
        !(i === 10 && !showActualDelivery);
    const allIcons = [
        STEP_ICONS[0],
        STEP_ICONS[1],
        STEP_ICONS[2],
        STEP_ICONS[3],
        STEP_ICONS[4],
        SchedulingIcon,
        STEP_ICONS[5],
        STEP_ICONS[6],
        STEP_ICONS[7],
        STEP_ICONS[8],
        STEP_ICONS[2],
    ];
    const actuals = allActuals.filter(keep);
    const labels = allLabels.filter(keep);
    const offsets = allOffsets.filter(keep);
    const icons = allIcons.filter(keep);

    // Find the last milestone index that has an actual date.
    // Steps before it without data show "N/A" — they happened, we just don't have timestamps.
    // Steps after it are estimated forward from that last actual date.
    let lastActualIndex = 0; // start_date is always actual so minimum is 0
    let lastActualDate: Date = startDate;
    for (let i = 0; i < actuals.length; i++) {
        if (actuals[i] !== null) {
            lastActualIndex = i;
            lastActualDate = actuals[i]!;
        }
    }

    // Build milestone display entries, cascading estimates forward from lastActualDate
    let futureAnchor = lastActualDate;
    const actualDeliveryIndex = labels.indexOf("Actual Delivery");
    const milestoneDisplays: MilestoneDisplay[] = actuals.map((actual, i) => {
        if (actual !== null) {
            return { label: labels[i], date: actual, state: "actual" };
        }
        // Actual Delivery on a delivered project with no file date: N/A
        if (i === actualDeliveryIndex) {
            return { label: labels[i], date: null, state: "unavailable" };
        }
        if (labels[i] === "Earliest Possible Delivery" && committedDelivery) {
            return { label: labels[i], date: committedDelivery, state: "committed" };
        }
        if (i < lastActualIndex) {
            // A later step has an actual date so this step must have happened — just no timestamp
            return { label: labels[i], date: null, state: "unavailable" };
        }
        const projected = addDays(futureAnchor, offsets[i]);
        const estimated =
            committedDelivery && projected > committedDelivery ? committedDelivery : projected;
        futureAnchor = estimated;
        return { label: labels[i], date: estimated, state: "estimated" };
    });

    const milestoneItems = milestoneDisplays.map((m, i) => ({
        label: m.label,
        text:
            m.state === "actual" || m.state === "committed"
                ? formatDate(m.date!)
                : m.state === "unavailable"
                ? "N/A"
                : `Est. ${formatDate(m.date!)}`,
        textColor: m.state === "actual" || m.state === "committed" ? "var(--text-2)" : "var(--muted)",
        fontStyle: m.state === "estimated" ? "italic" : "normal",
        state: m.state,
        icon: icons[i]
    }));

    return <HorizontalMilestoneStepper milestones={milestoneItems} />;
};

// ─── Project Pricing Card ────────────────────────────────────────────────────

interface ProjectPricingCardProps {
    project: BrandProject;
}

export const ProjectPricingCard: FC<ProjectPricingCardProps> = ({ project }) => {
    const { user, role } = useUser();

    // Mirrors the gate inside PricingBreakdown so the "Pricing" header
    // doesn't sit above an empty card.
    if (hidePricing(role, user, project)) {
        return null;
    }

    return (
        <div>
            <h2 className="text-lg font-extrabold leading-[120%] text-text-1 mb-4">Pricing</h2>
            <PricingBreakdown
                pricingTotal={project.pricing_total}
                pricingBreakdown={project.pricing_breakdown}
                pricingSnapshottedAt={project.pricing_snapshotted_at}
                defaultExpanded={false}
                project={project}
            />
        </div>
    );
};
